// src/pages/items/AddProduct.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import { collection, doc, getDocs, serverTimestamp, setDoc } from "firebase/firestore";
import { getDownloadURL, ref as storageRef, uploadBytes } from "firebase/storage";
import { useNavigate } from "react-router-dom";
import { db, storage } from "../../../firebaseConfig";
import { useAuth } from "../../../context/AuthContext";
import { ArrowRightIcon, CheckCircleIcon, XCircleIcon } from "@heroicons/react/24/outline";

/* ✅ ألوان الصورة فقط (Grayscale) */
const BRAND = {
  ink: "#2F2F2F",
  graphite: "#878787",
  silver: "#B8B8B8",
  mist: "#D0D0D0",
  paper: "#EFEFEF",
  white: "#FCFCFC",
};

export default function AddProduct() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [categories, setCategories] = useState([]);
  const [loadingCats, setLoadingCats] = useState(true);
  const [saving, setSaving] = useState(false);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // حقول النموذج
  const [name, setName] = useState("");
  const [qty, setQty] = useState("");
  const [barcode, setBarcode] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [price, setPrice] = useState("");
  const [description, setDescription] = useState("");

  // صورة المنتج
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const fileInputRef = useRef(null);

  const ui = useMemo(() => {
    return {
      pageBg: `radial-gradient(1200px 520px at 90% -30%, ${BRAND.paper}, ${BRAND.white} 55%)`,
      cardBorder: `1px solid ${BRAND.paper}`,
      cardShadow: "0 18px 50px rgba(0,0,0,0.06), 0 6px 18px rgba(0,0,0,0.04)",
      headerBg: `linear-gradient(135deg, ${BRAND.ink}, ${BRAND.graphite})`,
      btnBg: `linear-gradient(135deg, ${BRAND.ink}, ${BRAND.graphite})`,
      ring: "0 0 0 4px rgba(135,135,135,0.18)",
      inputBorder: BRAND.mist,
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoadingCats(true);
      setError("");
      try {
        const snap = await getDocs(collection(db, "categories"));
        const list = snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
        list.sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""), "ar"));
        if (mounted) setCategories(list);
      } catch {
        if (mounted) setError("تعذّر جلب قائمة الأقسام.");
      } finally {
        if (mounted) setLoadingCats(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  // تنظيف blob preview
  useEffect(() => {
    return () => {
      try {
        if (imagePreview && String(imagePreview).startsWith("blob:")) {
          URL.revokeObjectURL(imagePreview);
        }
      } catch {}
    };
  }, [imagePreview]);

  function handleBack() {
    if (window.history.length > 1) navigate(-1);
    else navigate("/");
  }

  function handleImageChange(e) {
    setError("");
    const file = e.target.files?.[0];
    if (!file) {
      setImageFile(null);
      setImagePreview("");
      return;
    }
    if (!file.type.startsWith("image/")) {
      setError("الملف يجب أن يكون صورة.");
      return;
    }
    if (file.size > 6 * 1024 * 1024) {
      setError("حجم الصورة كبير (الحد 6MB).");
      return;
    }
    setImageFile(file);
    const url = URL.createObjectURL(file);
    setImagePreview(url);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!user?.uid) {
      setError("الرجاء تسجيل الدخول أولًا.");
      return;
    }
    if (!name.trim() || !qty || !categoryId || price === "") {
      setError("الرجاء تعبئة الحقول المطلوبة (*)");
      return;
    }

    const qtyNum = Number(qty);
    const priceNum = Number(price);
    if (!Number.isFinite(qtyNum) || qtyNum < 0) {
      setError("العدد يجب أن يكون رقمًا صالحًا (0 أو أكثر).");
      return;
    }
    if (!Number.isFinite(priceNum) || priceNum < 0) {
      setError("السعر يجب أن يكون رقمًا صالحًا (0 أو أكثر).");
      return;
    }

    try {
      setSaving(true);

      // جهّز مستند المنتج أولًا للحصول على id
      const productsCol = collection(db, "users", user.uid, "products");
      const productRef = doc(productsCol);

      let imageURL = null;
      let imagePath = null;

      if (imageFile) {
        const sanitized = (name || "product")
          .toLowerCase()
          .replace(/\s+/g, "-")
          .replace(/[^a-z0-9\-]/g, "")
          .slice(0, 40);

        imagePath = `users/${user.uid}/products/${productRef.id}/${sanitized}-${Date.now()}`;
        const ref = storageRef(storage, imagePath);

        await uploadBytes(ref, imageFile, { contentType: imageFile.type });
        imageURL = await getDownloadURL(ref);
      }

      const payload = {
        name: name.trim(),
        quantity: qtyNum,
        barcode: barcode.trim() || null,
        categoryId,
        price: priceNum,
        description: description.trim() || null,
        imageURL: imageURL || null,
        imagePath: imagePath || null,
        createdAt: serverTimestamp(),
        ownerId: user.uid,
      };

      await setDoc(productRef, payload);

      setSuccess("تمت إضافة المنتج بنجاح.");
      setName("");
      setQty("");
      setBarcode("");
      setCategoryId("");
      setPrice("");
      setDescription("");
      setImageFile(null);
      setImagePreview("");
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (e2) {
      console.error(e2);
      setError("حدث خطأ أثناء إضافة المنتج.");
    } finally {
      setSaving(false);
    }
  }

  const inputBase =
    "w-full rounded-2xl border px-3 py-3 outline-none transition font-tajawal bg-white";

  return (
    <div dir="rtl" className="min-h-screen px-4 py-8" style={{ background: ui.pageBg }}>
      <div className="mx-auto max-w-3xl space-y-6">
        {/* رأس الصفحة */}
        <header
          className="relative overflow-hidden rounded-3xl p-6"
          style={{ background: ui.headerBg, boxShadow: ui.cardShadow }}
        >
          <div className="flex items-center justify-between gap-4">
            <button
              onClick={handleBack}
              className="group inline-flex items-center gap-2 rounded-2xl px-3 py-2 transition"
              style={{
                background: "rgba(255,255,255,0.10)",
                border: "1px solid rgba(255,255,255,0.18)",
                color: BRAND.white,
                fontFamily: "Tajawal, system-ui, sans-serif",
              }}
              title="رجوع"
            >
              <ArrowRightIcon className="h-5 w-5 transition group-hover:translate-x-0.5" />
              <span className="text-sm font-extrabold">رجوع</span>
            </button>

            <div className="text-center flex-1">
              <h1
                className="text-2xl font-extrabold text-white"
                style={{ fontFamily: "Tajawal, system-ui, sans-serif" }}
              >
                إضافة منتج
              </h1>
              <div
                className="mt-1 text-xs"
                style={{ color: "rgba(255,255,255,0.80)", fontFamily: "Tajawal, system-ui, sans-serif" }}
              >
                أدخل البيانات ثم احفظ — التصميم يعتمد ألوان الصورة فقط
              </div>
            </div>

            <span className="w-[84px]" />
          </div>
        </header>

        {/* بطاقة النموذج */}
        <div
          className="rounded-3xl overflow-hidden bg-white"
          style={{ border: ui.cardBorder, boxShadow: ui.cardShadow }}
        >
          <form onSubmit={handleSubmit} className="p-6">
            {/* تنبيه نجاح/خطأ */}
            {!!error && (
              <div
                className="mb-4 flex items-start gap-2 rounded-2xl px-4 py-3"
                style={{
                  background: BRAND.paper,
                  border: `1px solid ${BRAND.mist}`,
                  color: BRAND.ink,
                }}
              >
                <XCircleIcon className="mt-[2px] h-5 w-5" style={{ color: BRAND.graphite }} />
                <span className="text-sm font-tajawal">{error}</span>
              </div>
            )}

            {!!success && (
              <div
                className="mb-4 flex items-start gap-2 rounded-2xl px-4 py-3"
                style={{
                  background: BRAND.white,
                  border: `1px solid ${BRAND.mist}`,
                  color: BRAND.ink,
                }}
              >
                <CheckCircleIcon className="mt-[2px] h-5 w-5" style={{ color: BRAND.ink }} />
                <span className="text-sm font-tajawal">{success}</span>
              </div>
            )}

            {/* الشبكة */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {/* الاسم */}
              <div>
                <label className="mb-2 block text-sm font-extrabold font-tajawal" style={{ color: BRAND.ink }}>
                  اسم المنتج *
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="مثال: مشروب غازي 250مل"
                  required
                  className={inputBase}
                  style={{ borderColor: ui.inputBorder, color: BRAND.ink }}
                  onFocus={(e) => (e.currentTarget.style.boxShadow = ui.ring)}
                  onBlur={(e) => (e.currentTarget.style.boxShadow = "none")}
                />
              </div>

              {/* العدد */}
              <div>
                <label className="mb-2 block text-sm font-extrabold font-tajawal" style={{ color: BRAND.ink }}>
                  العدد *
                </label>
                <input
                  type="number"
                  value={qty}
                  onChange={(e) => setQty(e.target.value)}
                  min={0}
                  required
                  placeholder="0"
                  className={inputBase}
                  style={{ borderColor: ui.inputBorder, color: BRAND.ink }}
                  onFocus={(e) => (e.currentTarget.style.boxShadow = ui.ring)}
                  onBlur={(e) => (e.currentTarget.style.boxShadow = "none")}
                />
              </div>

              {/* السعر */}
              <div>
                <label className="mb-2 block text-sm font-extrabold font-tajawal" style={{ color: BRAND.ink }}>
                  السعر (د.ع) *
                </label>
                <input
                  type="number"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  min={0}
                  step="0.01"
                  required
                  placeholder="0"
                  className={inputBase}
                  style={{ borderColor: ui.inputBorder, color: BRAND.ink }}
                  onFocus={(e) => (e.currentTarget.style.boxShadow = ui.ring)}
                  onBlur={(e) => (e.currentTarget.style.boxShadow = "none")}
                />
              </div>

              {/* الباركود */}
              <div>
                <label className="mb-2 block text-sm font-extrabold font-tajawal" style={{ color: BRAND.ink }}>
                  الباركود (اختياري)
                </label>
                <input
                  type="text"
                  value={barcode}
                  onChange={(e) => setBarcode(e.target.value)}
                  placeholder="مثال: 1234567890123"
                  className={inputBase}
                  style={{ borderColor: ui.inputBorder, color: BRAND.ink }}
                  onFocus={(e) => (e.currentTarget.style.boxShadow = ui.ring)}
                  onBlur={(e) => (e.currentTarget.style.boxShadow = "none")}
                />
              </div>

              {/* القسم */}
              <div className="md:col-span-2">
                <label className="mb-2 block text-sm font-extrabold font-tajawal" style={{ color: BRAND.ink }}>
                  القسم *
                </label>
                <select
                  value={categoryId}
                  onChange={(e) => setCategoryId(e.target.value)}
                  required
                  className={inputBase}
                  style={{ borderColor: ui.inputBorder, color: BRAND.ink }}
                  onFocus={(e) => (e.currentTarget.style.boxShadow = ui.ring)}
                  onBlur={(e) => (e.currentTarget.style.boxShadow = "none")}
                >
                  <option value="" disabled>
                    اختر قسمًا
                  </option>
                  {loadingCats && <option value="">جارٍ التحميل...</option>}
                  {!loadingCats && categories.length === 0 && (
                    <option value="" disabled>
                      لا توجد أقسام — أضف قسمًا أولًا
                    </option>
                  )}
                  {!loadingCats &&
                    categories.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name || c.id}
                      </option>
                    ))}
                </select>
              </div>

              {/* الوصف */}
              <div className="md:col-span-2">
                <label className="mb-2 block text-sm font-extrabold font-tajawal" style={{ color: BRAND.ink }}>
                  الوصف (اختياري)
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                  placeholder="وصف مختصر..."
                  className={inputBase}
                  style={{ borderColor: ui.inputBorder, color: BRAND.ink }}
                  onFocus={(e) => (e.currentTarget.style.boxShadow = ui.ring)}
                  onBlur={(e) => (e.currentTarget.style.boxShadow = "none")}
                />
              </div>

              {/* الصورة */}
              <div className="md:col-span-2">
                <label className="mb-2 block text-sm font-extrabold font-tajawal" style={{ color: BRAND.ink }}>
                  صورة المنتج (اختياري)
                </label>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className={inputBase}
                  style={{ borderColor: ui.inputBorder, color: BRAND.ink, paddingTop: 10, paddingBottom: 10 }}
                />

                {imagePreview ? (
                  <div className="mt-4 flex items-center gap-4">
                    <img
                      src={imagePreview}
                      alt="معاينة المنتج"
                      className="h-40 w-40 rounded-2xl object-cover"
                      style={{ border: `1px solid ${BRAND.paper}`, boxShadow: "0 10px 24px rgba(0,0,0,0.06)" }}
                    />
                    <div className="text-xs" style={{ color: BRAND.graphite, fontFamily: "Tajawal, system-ui, sans-serif" }}>
                      معاينة الصورة قبل الحفظ
                    </div>
                  </div>
                ) : null}
              </div>
            </div>

            {/* الأزرار */}
            <div className="mt-6 flex flex-col-reverse items-stretch gap-3 sm:flex-row sm:items-center sm:justify-between">
              <button
                type="submit"
                disabled={saving}
                className="inline-flex items-center justify-center rounded-2xl px-5 py-3 text-sm font-extrabold transition disabled:opacity-60"
                style={{
                  background: ui.btnBg,
                  color: BRAND.white,
                  boxShadow: "0 12px 28px rgba(0,0,0,0.10)",
                  fontFamily: "Tajawal, system-ui, sans-serif",
                }}
              >
                {saving ? "جارٍ الحفظ..." : "حفظ المنتج"}
              </button>

              <button
                type="button"
                onClick={handleBack}
                className="inline-flex items-center justify-center rounded-2xl px-5 py-3 text-sm font-extrabold transition"
                style={{
                  background: BRAND.white,
                  color: BRAND.ink,
                  border: `1px solid ${BRAND.mist}`,
                  fontFamily: "Tajawal, system-ui, sans-serif",
                }}
              >
                رجوع
              </button>
            </div>
          </form>

          {/* شريط سفلي */}
          <div className="h-1 w-full" style={{ background: BRAND.paper }} />
        </div>
      </div>
    </div>
  );
}
